import * as React from 'react';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import styled from 'styled-components';
import { ContentSpacing, Breakpoints } from '@estee/elc-base-theme';
import { SliderView, ISliderSettings } from '@estee/elc-slider';

import { classes } from '../../utils/Classes';
import { IShade, ISku } from '../../interfaces/IVto';
import { CUSTOM_SLIDES_TO_SHOW, CUSTOM_SLIDES_TO_SCROLL } from '../../constants/Vto';
import { VtoShadeItem } from './VtoShadeItem';

export interface IVtoCustomShadePicker {
    selectedSku: ISku;
    isPerfectShadeEnabled: boolean;
    sortPerfectLabels?(a: IShade, b: IShade): number;
    onShadeSelect(name: string, eventLabel: string): void;
    slidesToShow: number;
    slidesToScroll: number;
    showAllShadesVariant?: boolean;
    showPerfectShadesLabel?: boolean;
    isMatchSaved?: boolean;
    savedMatch?: string;
}

const CustomShadePicker = styled.div`
    margin-top: ${ContentSpacing.space6};
    .slick-slider {
        margin-bottom: 0;
    }
`;

@observer
export class VtoCustomShadePicker extends React.Component<IVtoCustomShadePicker> {
    private customShadePickerClassNames = classes('vto-custom-shade-picker', {
        'elc-vto-all-shades-variant': this.props.showAllShadesVariant
    });

    @computed
    public get customShades(): JSX.Element[] {
        const {
            selectedSku,
            isPerfectShadeEnabled,
            sortPerfectLabels,
            onShadeSelect,
            showAllShadesVariant,
            showPerfectShadesLabel,
            isMatchSaved,
            savedMatch
        } = this.props;

        const {
            parentProduct: { perfectShades, shades, displayName, productId },
            selectedShade
        } = selectedSku;

        const filteredShades =
            isPerfectShadeEnabled && perfectShades.length ? perfectShades : shades;

        if (isPerfectShadeEnabled && sortPerfectLabels) {
            filteredShades.sort(sortPerfectLabels);
        }

        return filteredShades.map((shade) => {
            const handleShadeClick = () => {
                const { name } = shade;
                const eventLabel = `${productId} - ${shade.parentSku?.perlgem?.SKU_BASE_ID} - ${displayName}`;

                if (name) {
                    onShadeSelect(name, eventLabel);
                }
            };

            return (
                <VtoShadeItem
                    key={`${shade.hexVal}-shade`}
                    shade={shade}
                    selectedShade={selectedShade}
                    isPerfectShadeEnabled={isPerfectShadeEnabled}
                    onClick={handleShadeClick}
                    showAllShadesVariant={showAllShadesVariant}
                    isMatchSaved={isMatchSaved}
                    showPerfectShadesLabel={showPerfectShadesLabel}
                    savedMatch={savedMatch}
                />
            );
        });
    }

    private get sliderSettings(): ISliderSettings {
        const { slidesToShow, slidesToScroll } = this.props;

        return {
            arrows: false,
            responsive: [
                {
                    breakpoint: Breakpoints.desktop,
                    settings: {
                        slidesToShow: slidesToShow || CUSTOM_SLIDES_TO_SHOW,
                        slidesToScroll: slidesToScroll || CUSTOM_SLIDES_TO_SCROLL
                    }
                }
            ]
        };
    }

    public render() {
        return (
            <CustomShadePicker className={this.customShadePickerClassNames}>
                <SliderView sliderSettings={this.sliderSettings} items={this.customShades} />
            </CustomShadePicker>
        );
    }
}
